import RichText from '@commons/components/RichText';
import type { BaseContainerProps } from '@commons/containers/BaseContainer';
import type { DrupalBlock, DrupalText } from '@commons/types/drupal';
import type { Breakpoint, Color, Spacing, Theme } from '@commons/types/styles';

// Type definition for the RichTextBlock component properties.

type Props = DrupalBlock<
  DrupalText & {
    border?: boolean;
    borderGap?: Spacing;
    borderColor?: Color | 'default';
    mobileBreakpoint?: Breakpoint;
    hideMobileBorder?: boolean;
    padding?: Spacing;
    theme?: Theme;
    blurredBackground?: boolean;
  }
>;

/**
 * RichText block component.
 */

const RichTextBlock = ({ content }: Props) => {
  if (!content?.value) return null;

  const { value = '', border = false, borderGap = 'xxs', hideMobileBorder = false } = content;

  // Get the mobile breakpoint or set it to `md` by default (e.g. if it's null or undefined).
  const mobileBreakpoint = content.mobileBreakpoint || 'md';

  const borderProp: BaseContainerProps['border'] = {
    xs: border && !hideMobileBorder ? 'top' : undefined,
    [mobileBreakpoint]: border ? 'left' : undefined,
  };

  // To ensure backward compatibility, any `null` value must be treated as `undefined`.
  const borderColor = content.borderColor ?? undefined;
  const parsedBorderColor = borderColor === 'default' ? undefined : borderColor;

  // Theme, padding, blurredBackground should only be used when there's no border.
  const theme = content.theme ?? undefined;
  const padding = content.padding ?? undefined;
  const blurredBackground = content.blurredBackground ?? false;

  return (
    <RichText
      value={value}
      theme={!border ? theme : undefined}
      padding={!border ? padding : undefined}
      border={borderProp}
      borderColor={parsedBorderColor}
      paddingTop={{
        xs: border && !hideMobileBorder ? borderGap : 'none',
        [mobileBreakpoint]: 'none',
      }}
      paddingLeft={{ [mobileBreakpoint]: border ? borderGap : undefined }}
      blurredBackground={blurredBackground}
      className="w-100"
    />
  );
};

export default RichTextBlock;
